import { useContext, useState } from 'react';
import { AdminFormModal, ModalStage } from '../../../AdminFormModal';
import { CheckBox, Input, InputDropdown } from '@platform/components';
import { AdminTableContext } from '../../../AdminTable';
import { AdminTeams } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateTeamAssignmentRequestOrganizationDecision } from '@platform/client/schemas';

export const ProjectAssignmentModal = ({
  teamId,
  cohortId,
  onClose,
  currentProjectId,
  currentOrganizationDecision,
}: {
  teamId: number;
  cohortId: number;
  onClose: () => void;
  currentProjectId?: number;
  currentOrganizationDecision?: UpdateTeamAssignmentRequestOrganizationDecision;
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [projectId, setProjectId] = useState(currentProjectId);
  const [organizationDecision, setOrganizationDecision] =
    useState<UpdateTeamAssignmentRequestOrganizationDecision>(
      currentOrganizationDecision ?? 'pending',
    );
  const [isDeleteRequest, setIsDeleteRequest] = useState<boolean>(false);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const mutationOptions = {
    onMutate: () => {
      setModalStage('submitting');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AdminTeams.getGetTeamsQueryKey({
          cohort_id: cohortId,
          page: adminQueryOptionsV2.page,
          limit: adminQueryOptionsV2.limit,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: AdminTeams.getGetTeamDetailsQueryKey({ team_id: teamId }),
      });
      setModalStage('success');
    },
    onError: () => {
      setModalStage('error');
    },
  };
  const assignProjectMutator = AdminTeams.useAssignTeamToProject({
    mutation: mutationOptions,
  });
  const deleteAssignmentMutator = AdminTeams.useUnassignTeamFromProject({
    mutation: mutationOptions,
  });
  const updateAssignmentMutator = AdminTeams.useUpdateTeamAssignment({
    mutation: mutationOptions,
  });
  const notificationDescription =
    organizationDecision === UpdateTeamAssignmentRequestOrganizationDecision.accepted ?
      'Send match confirmation email'
    : 'Send organization decision email';
  return (
    <AdminFormModal
      title={`Manage Project for Team: #${teamId}`}
      modalStage={modalStage}
      onClose={onClose}
      submitCTA={{
        text: 'Save',
        callback: () => {
          if (currentProjectId) {
            if (isDeleteRequest) {
              deleteAssignmentMutator.mutate({
                data: {
                  teamId,
                  projectId: currentProjectId,
                },
              });
            } else {
              updateAssignmentMutator.mutate({
                data: {
                  teamId,
                  projectId: currentProjectId,
                  sendEmail,
                  organizationDecision,
                },
              });
            }
          } else if (projectId) {
            assignProjectMutator.mutate({
              data: {
                teamId,
                projectId: projectId,
                sendEmail,
                organizationDecision,
              },
            });
          }
        },
        isDisabled: () => !projectId && !isDeleteRequest,
      }}
    >
      <div className='flex flex-col gap-3'>
        {currentProjectId && (
          <CheckBox
            description='Delete current assignment'
            size='md'
            onChange={(isSelected) => setIsDeleteRequest(isSelected)}
          />
        )}
        {!isDeleteRequest && (
          <>
            <Input
              label={`${currentProjectId ? 'Project ID' : 'New project ID'}`}
              placeholder='Enter project ID'
              type='inline'
              inputType='number'
              value={projectId?.toString() ?? ''}
              onChange={(e) => setProjectId(parseInt(e.target.value))}
              disabled={!!currentProjectId}
            />
            <RepDecisionDropdown
              onSelectionChange={(key) =>
                setOrganizationDecision(key as UpdateTeamAssignmentRequestOrganizationDecision)
              }
              selectedKey={organizationDecision}
            />
            {organizationDecision !== UpdateTeamAssignmentRequestOrganizationDecision.declined && (
              <CheckBox
                description={notificationDescription}
                size='md'
                onChange={(isSelected) => setSendEmail(isSelected)}
                additionalClassnames='mt-6'
              />
            )}
          </>
        )}
      </div>
    </AdminFormModal>
  );
};

const RepDecisionDropdown = ({
  selectedKey,
  onSelectionChange,
}: {
  selectedKey: UpdateTeamAssignmentRequestOrganizationDecision;
  onSelectionChange: (key: UpdateTeamAssignmentRequestOrganizationDecision) => void;
}) => {
  return (
    <InputDropdown
      type='default'
      inputDisabled
      label='Organization decision'
      items={[
        {
          text: 'Accepted',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.accepted,
        },
        {
          text: 'Pending',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.pending,
        },
        {
          text: 'Declined',
          textValue: UpdateTeamAssignmentRequestOrganizationDecision.declined,
        },
      ]}
      onSelectionChange={(key) =>
        onSelectionChange(key as UpdateTeamAssignmentRequestOrganizationDecision)
      }
      defaultSelectedKey={selectedKey}
    />
  );
};
